import { useState } from "react";
import { RotateCcw, Upload } from "lucide-react";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { imgSrc } from "@/lib/content";
import { FieldError } from "@/components/form/ConfigField";

const IMG_ERR = "Formato non supportato o file troppo grande (max 5 MB)";
const ALT_ERR = "Inserisci il testo alternativo dell'immagine";
const MIME = { jpg: "image/jpeg", png: "image/png", webp: "image/webp" };

export const ImageCard = ({ slot, img, readOnly, onDone }) => {
    const id = `cms-img-${slot}`;
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [alt, setAlt] = useState(img.alt || "");
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const pick = (e) => {
        const f = e.target.files?.[0];
        setStatus(null);
        if (!f) return;
        const ok = img.types.map((t) => MIME[t]).includes(f.type) && f.size <= 5 * 1024 * 1024;
        setErrors(ok ? {} : { file: IMG_ERR });
        setFile(ok ? f : null);
        setPreview(ok ? URL.createObjectURL(f) : null);
        if (!ok) e.target.value = "";
    };
    const save = async () => {
        if (img.alt_required && !alt.trim()) { setErrors({ alt: ALT_ERR }); return; }
        const fd = new FormData();
        fd.append("alt", alt);
        if (file) fd.append("file", file);
        setBusy(true);
        try { await api.post(`/admin/content/image/${slot}`, fd); setErrors({}); setFile(null); setPreview(null); setStatus({ kind: "success", text: "Contenuto pubblicato" }); onDone(); }
        catch (err) { const fe = fieldErrors(err); setErrors(fe || {}); setStatus(fe ? null : { kind: "error", text: errorMessage(err) }); }
        setBusy(false);
    };
    const restore = async () => {
        try { const { data } = await api.delete(`/admin/content/image/${slot}`); setAlt(data.image.alt); setFile(null); setPreview(null); setErrors({}); setStatus({ kind: "success", text: data.message }); onDone(); }
        catch (err) { setStatus({ kind: "error", text: errorMessage(err) }); }
    };
    const dirty = !!file || alt !== (img.alt || "");
    return (
        <section className="edi-card p-5 sm:p-6 space-y-4" aria-labelledby={`${id}-title`} data-testid={`${id}-card`}>
            <div>
                <h3 id={`${id}-title`} className="text-lg font-bold">{img.label}</h3>
                <p className="text-sm text-edi-muted" data-testid={`${id}-hint`}>{img.hint} · {img.types.map((t) => t.toUpperCase()).join(", ")} · max 5 MB</p>
            </div>
            <div className={`rounded-xl border border-edi-border bg-edi-bg p-3 flex items-center justify-center ${slot === "favicon" ? "w-28 h-28" : "h-48"}`}>
                <img src={preview || imgSrc(img.url)} alt={`Anteprima: ${img.label}`} className="max-h-full max-w-full object-contain" data-testid={`${id}-preview`} />
            </div>
            {!readOnly && (
                <div className="space-y-1">
                    <label htmlFor={`${id}-file`} className="edi-label">Nuova immagine</label>
                    <input id={`${id}-file`} type="file" accept={img.types.map((t) => MIME[t]).join(",")} onChange={pick} className="block w-full text-sm"
                        aria-invalid={errors.file ? "true" : "false"} aria-describedby={errors.file ? `${id}-file-error` : undefined} data-testid={`${id}-file-input`} />
                    <FieldError id={`${id}-file-error`} error={errors.file} />
                </div>
            )}
            {(img.alt_required || slot === "social") && (
                <div className="space-y-1">
                    <label htmlFor={`${id}-alt`} className="edi-label">Testo alternativo{img.alt_required && <span aria-hidden="true"> *</span>}</label>
                    <input id={`${id}-alt`} className="edi-input" value={alt} readOnly={readOnly} maxLength={200} onChange={(e) => setAlt(e.target.value)} aria-required={img.alt_required}
                        aria-invalid={errors.alt ? "true" : "false"} aria-describedby={errors.alt ? `${id}-alt-error` : undefined} data-testid={`${id}-alt-input`} />
                    <FieldError id={`${id}-alt-error`} error={errors.alt} />
                </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
                {!readOnly && <button type="button" className="btn-primary py-2" disabled={!dirty || busy} onClick={save} data-testid={`${id}-save`}><Upload aria-hidden="true" size={16} /> {busy ? "Caricamento…" : "Pubblica"}</button>}
                {!readOnly && img.custom && <button type="button" className="btn-secondary py-2" onClick={restore} data-testid={`${id}-restore`}><RotateCcw aria-hidden="true" size={16} /> Ripristina originale</button>}
                {status && <p role="status" className={`text-sm font-semibold ${status.kind === "error" ? "text-edi-error" : "text-edi-success"}`} data-testid={`${id}-status`}>{status.text}</p>}
            </div>
        </section>
    );
};
